import React from 'react';
import { alpha, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  Cancel,
  CheckCircle,
  ExpandMore,
  HelpOutline,
  Search,
  WarningAmber,
} from '@mui/icons-material';

const rows = [
  {
    title: 'State Change',
    openedBy: 'Opened by Alex Carter',
    formulation: 'Blueberry Variant V3',
    from: 'idea',
    to: 'prototype',
    review: 'Not reviewed',
    status: 'pending',
  },
  {
    title: 'Prototype Review',
    openedBy: 'Opened by John Snow',
    formulation: 'High-Protein Trial V2',
    from: 'idea',
    to: 'prototype',
    review: 'Approved',
    status: 'approved',
  },
  {
    title: 'Scale-up Request',
    openedBy: 'Opened by Sophia Smith',
    formulation: 'Pilot Batch V4',
    from: 'prototype',
    to: 'scale-up',
    review: 'Not reviewed',
    status: 'pending',
  },
  {
    title: 'Production Gate',
    openedBy: 'Opened by QA Team',
    formulation: 'Final Formula V5',
    from: 'scale-up',
    to: 'production',
    review: 'Rejected',
    status: 'rejected',
  },
];

const stageColors = {
  idea: '#006BFF',
  prototype: '#148CFF',
  'scale-up': '#FF2D2D',
  production: '#43C936',
};

const TABLE_COLUMNS =
  'minmax(110px, 1.05fr) minmax(120px, 1.1fr) minmax(175px, 1.35fr) minmax(125px, 1fr)';

const tableGridSx = {
  display: 'grid',
  gridTemplateColumns: TABLE_COLUMNS,
  columnGap: 1.25,
  alignItems: 'center',
};

function StageChip({ label }) {
  const color = stageColors[label] || '#006BFF';

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 22,
        flexShrink: 0,
        fontWeight: 700,
        fontSize: 10.5,
        color,
        borderColor: color,
        bgcolor: alpha(color, 0.04),
        '& .MuiChip-label': {
          px: 0.85,
        },
      }}
    />
  );
}

function ReviewStatus({ status, label }) {
  const config =
    {
      pending: {
        icon: <HelpOutline sx={{ fontSize: 16, flexShrink: 0 }} />,
        color: '#F4B400',
        bgcolor: '#FFE7A3',
      },
      approved: {
        icon: <CheckCircle sx={{ fontSize: 16, flexShrink: 0 }} />,
        color: '#2FBF3A',
        bgcolor: '#DDF8DF',
      },
      rejected: {
        icon: <Cancel sx={{ fontSize: 16, flexShrink: 0 }} />,
        color: '#F04438',
        bgcolor: '#FFE1DE',
      },
    }[status] || {
      icon: <HelpOutline sx={{ fontSize: 16, flexShrink: 0 }} />,
      color: '#64748B',
      bgcolor: '#EEF2F6',
    };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        width: 'fit-content',
        maxWidth: '100%',
        px: 1.15,
        py: 0.55,
        borderRadius: 999,
        bgcolor: config.bgcolor,
        color: config.color,
        fontWeight: 800,
        fontSize: 12,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {config.icon}
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Box>
    </Stack>
  );
}

function ReviewLoopVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 280,
        p: 2.5,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.5,
              py: 0.75,
              border: '1px solid',
              borderColor: alpha('#006BFF', 0.35),
              borderRadius: 1.5,
              color: 'primary.main',
              fontWeight: 800,
              fontSize: 12,
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span">Any Review State</Box>
            <ExpandMore sx={{ fontSize: 16 }} />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              width: 150,
              px: 1.5,
              py: 0.75,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              color: 'text.secondary',
            }}
          >
            <Search sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography variant="caption">Search</Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            ...tableGridSx,
            px: 2,
            py: 1.25,
            bgcolor: alpha('#0F172A', 0.02),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {['Information', 'Formulation', 'States', 'Review'].map((header) => (
            <Typography
              key={header}
              variant="caption"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              {header}
            </Typography>
          ))}
        </Box>

        {rows.map((row, index) => (
          <Box
            key={`${row.title}-${row.formulation}`}
            sx={{
              ...tableGridSx,
              px: 2,
              py: 1.35,
              borderBottom: index < rows.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                {row.status === 'pending' && (
                  <WarningAmber sx={{ fontSize: 15, color: '#F4B400', flexShrink: 0 }} />
                )}

                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 0,
                    fontWeight: 800,
                    color: 'primary.main',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.title}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  fontSize: 10.5,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.openedBy}
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: 'primary.main',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.formulation}
            </Typography>

            <Stack
              direction="row"
              spacing={0.65}
              alignItems="center"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StageChip label={row.from} />

              <Box
                sx={{
                  width: 14,
                  height: '1px',
                  flexShrink: 0,
                  bgcolor: 'divider',
                }}
              />

              <StageChip label={row.to} />
            </Stack>

            <Box
              sx={{
                minWidth: 0,
                justifySelf: 'start',
                overflow: 'hidden',
              }}
            >
              <ReviewStatus status={row.status} label={row.review} />
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

export default ReviewLoopVisual;